import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useCustomerStore } from '@stores/me/customer'
import { useOptionsStore } from '@stores/options'

import amplitudeConfig from '@config/amplitude.json'

import { trackEvent } from '@tools/amplitude'

type UseDisciplineLinkOptions = {
  discipline: Discipline
  isDiscipline: ComputedRef<boolean>
  isRouteDiscipline: ComputedRef<boolean>
  homeRoute: ComputedRef<string | null>
  trackName: string
  text: string
}

type UseDisciplineLinkReturn = {
  discount: ComputedRef<number>
  setRedirect: () => Promise<void>
  handleClick: (event: MouseEvent) => void
}

export const useDisciplineLink = ({
  discipline,
  isDiscipline,
  isRouteDiscipline,
  homeRoute,
  trackName,
  text,
}: UseDisciplineLinkOptions): UseDisciplineLinkReturn => {
  const customerStore = useCustomerStore()
  const optionsStore = useOptionsStore()

  const disciplineData = computed(() =>
    customerStore.getActiveKidDisciplines.find(d => d.code === discipline) || null,
  )

  const discount = computed(() =>
    ((!isDiscipline.value || (disciplineData.value != null && !disciplineData.value.demo?.id && !disciplineData.value.is_paid)) && customerStore.isDeal)
      ? optionsStore.getUpsellDiscount
      : 0,
  )

  const setRedirect = async (): Promise<void> => {
    if (!homeRoute.value) return

    trackEvent(amplitudeConfig.events.clickLink, {
      name: trackName,
      link_text: text,
      href: homeRoute.value,
    })

    await navigateTo(homeRoute.value)
  }

  // The item links to the discipline home: route in-app, and ignore the current discipline, as its disabled item did.
  const handleClick = (event: MouseEvent): void => {
    event.preventDefault()
    if (isRouteDiscipline.value) return

    void setRedirect()
  }

  return {
    discount,
    setRedirect,
    handleClick,
  }
}
